import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { BlService } from 'src/app/_services/bl/bl.service';
import { LoteService } from 'src/app/_services/lote/lote.service';
import { Location } from '@angular/common';
import { LoteCabotajeService } from 'src/app/_services/loteCabotaje/lote-cabotaje.service';
import { LocalStorageService } from 'src/app/_services/localStorage/local-storage.service';
import { AppointmentsService } from 'src/app/_services/appointments/appointments.service';
import { Authorizations } from 'src/app/interfaces/auth.interfaces';
import { Subscription } from 'rxjs';

/**
 * Componente de menú lateral que muestra las opciones de navegación según las
 * autorizaciones del usuario, recarga los listados de cada módulo, indica las
 * citas pendientes en tiempo real (SSE) y gestiona el cierre de sesión.
 */
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.css']
})
export class SidenavComponent implements OnInit, OnDestroy {
  authorizations: Authorizations = {
    menu_options: [
      {
        menu_id: 1,
        name: "boat_list",
        description: "Listado de barcos",
        router_link: "ship-list"
      },
      {
        menu_id: 3,
        name: "export",
        description: "Exportación",
        router_link: "exportation-batch-list"
      },
      {
        menu_id: 4,
        name: "cabotage",
        description: "Cabotaje",
        router_link: "cabotage-batch-list"
      },
      {
        menu_id: 5,
        name: "reports",
        description: "Reportes",
        router_link: "reports"
      },
      {
        menu_id: 6,
        name: "catalog_management",
        description: "Administración de catálogos",
        router_link: "catalogs"
      }
    ],
    list_actions: []
  };
  isAduana: boolean = false;
  pendingAppointments: number = 0;
  profile: string = '';
  private sseSubscription?: Subscription;

  constructor(
    private blServices: BlService,
    private loteService: LoteService,
    private loteCabotajeService: LoteCabotajeService,
    private localStorageServices: LocalStorageService,
    public authService: AuthService,
    private appointmentsService: AppointmentsService,
    private router: Router,
    private location: Location
  ) {
    const auth = this.authService.getAuthorizationsObj();
    this.authorizations = typeof auth !== 'string' ? auth : this.authorizations;
    this.authorizations.menu_options = this.authorizations.menu_options.filter((option) => {
      return option.menu_id !== 10
    });
  }

  ngOnInit(): void {
    this.isAduana = this.localStorageServices.getData('currentUser')['cognito:groups'][0] === 'aduana' || false;
    this.profile = this.localStorageServices.getData('currentUser')['cognito:groups'][0] || '';

    // Suscribirse al SSE solo si el perfil no es cliente_asipona
    if (this.profile !== 'cliente_asipona') {
      this.sseSubscription = this.appointmentsService.getPendingAppointmentsSSE().subscribe({
        next: (data) => {
          this.pendingAppointments = data.pending_count;
        },
        error: (error) => {
          console.error('Error en SSE de citas pendientes:', error);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
  }

  get path(): string {
    return this.blServices.path;
  }

  /**
   * Cierra la sesión del usuario actual.
   */
  logout() {
    this.authService.logout();
  }

  /**
   * Recarga o navega al listado del módulo correspondiente al identificador de menú indicado.
   * @param id - Identificador del menú a recargar.
   */
  reload(id: number) {
    switch (id) {
      case 1:
        this.reloadListadoLotes();
        break;
      case 3:
        this.reloadListadoLotesExportacion();
        break;
      case 4:
        this.reloadListadoLotesCabotaje();
        break;
      case 5:
        this.reloadReports();
        break;
      case 6:
        this.reloadCatalogs();
        break;
      case 7:
        this.reloadBoletas();
        break;
      case 8:
        this.reloadAuthorizations();
        break;
      case 9:
        this.reloadAppointments();
        break;
      case 11:
        this.reloadDec();
        break;
      case 12:
        this.reloadChatbot();
        break;
      default:
        this.reloadListadoLotes();
        break;
    }
  }

  /**
   * Devuelve la clase del ícono asociada al identificador de menú indicado.
   * @param id - Identificador del menú.
   * @returns La clase CSS del ícono correspondiente.
   */
  getClass(id: number) {
    switch (id) {
      case 1:
        return 'bi bi-house';
      case 2:
        return 'bi bi-box-seam';
      case 3:
        return 'bi bi-boxes';
      case 4:
        return 'bi bi-bounding-box';
      case 5:
        return 'bi bi-clipboard-data';
      case 6:
        return 'bi bi-list-columns-reverse';
      case 7:
        return 'bi bi-truck';
      case 8:
        return 'bi bi-person-gear';
      case 9:
        return 'bi bi-calendar4-week';
      case 11:
        return 'bi bi-journal-arrow-down';
      case 12:
        return 'bi bi-chat-right-dots';
      default:
        return 'bi bi-house';
    }
  }

  reloadListadoLotes() {
    if (this.router.url === '/ship-list') {
      this.blServices.textFilter = '';
      this.blServices.getBlTable();
    } else {
      this.router.navigate(['/ship-list']);
    }
  }

  reloadListadoLotesExportacion() {
    if (this.router.url.includes('/exportation') && !this.router.url.includes('batch-movement')) {
      this.blServices.textFilter = '';
      this.loteService.getLotesExportacionTabla(this.loteService.exportBatchesYear);
    } else {
      this.router.navigate(['/exportation']);
    }
  }

  reloadListadoLotesCabotaje() {
    if (this.router.url.includes('/cabotage') && !this.router.url.includes('batch-movement')) {
      this.blServices.textFilter = '';
      this.loteCabotajeService.getLotesCabotajeTabla(this.loteCabotajeService.cabotageBatchesYear);
    } else {
      this.router.navigate(['/cabotage']);
    }
  }

  reloadReports() {
    if (this.router.url.includes('/reports')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/reports']);
    }
  }

  reloadCatalogs() {
    if (this.router.url.includes('/catalogs')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/catalogs']);
    }
  }

  reloadBoletas() {
    if (this.router.url.includes('/scale-ticket')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/scale-ticket']);
    }
  }

  reloadAuthorizations() {
    if (this.router.url.includes('/segurity')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/segurity']);
    }
  }

  reloadAppointments() {
    if (this.router.url === '/appointments') {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/appointments']);
    }
  }

  reloadDec() {
    if (this.router.url.includes('/dec/list')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/dec']);
    }
  }

  reloadChatbot() {
    if (this.router.url.includes('/chatbot')) {
      this.blServices.textFilter = '';
      this.reloadComponent();
    } else {
      this.router.navigate(['/chatbot']);
    }
  }

  /**
   * Recarga el componente de la ruta actual forzando una nueva navegación.
   */
  reloadComponent() {
    const currentRoute = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentRoute]);
    });
  }

  /**
   * Regresa a la ruta previa configurada o, en su defecto, al estado anterior del navegador.
   */
  goBack(): void {
    if (!this.path) {
      this.location.back();
    } else {
      this.router.navigate([this.path]);
    }
  }
}
